import { Cable, ServerCog, BatteryCharging, Radio, Move } from "lucide-react";
import { Reveal, SectionHead } from "@/components/Reveal";

const TOWER = "https://images.unsplash.com/photo-1789222707898-94898e53f4a5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwzfHx0ZWxlY29tJTIwdG93ZXIlMjBzdW5zZXQlMjBza3l8ZW58MHx8fHwxNzkxMTIwNjQwfDA&ixlib=rb-4.1.0&q=85";
const RACKS = "https://images.unsplash.com/photo-1544197150-b99a580bb7a8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwyfHxuZXR3b3JrJTIwc3dpdGNoJTIwc2VydmVyJTIwcmFjayUyMGRhdGElMjBjZW50ZXJ8ZW58MHx8fHwxNzkxMTIwNjQwfDA&ixlib=rb-4.1.0&q=85";
const WIRING = "https://images.unsplash.com/photo-1682559736721-c2e77ff4c650?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwzfHxuZXR3b3JrJTIwc3dpdGNoJTIwc2VydmVyJTIwcmFjayUyMGRhdGElMjBjZW50ZXJ8ZW58MHx8fHwxNzkxMTIwNjQwfDA&ixlib=rb-4.1.0&q=85";

const Card = ({ testid, title, desc, icon: Icon, image, alt, className = "" }) => (
  <div
    data-testid={testid}
    className={`group relative overflow-hidden rounded-3xl border border-white/[0.08] bg-navy-800 ring-1 ring-white/5 transition-all duration-300 hover:border-cyan-400/30 hover:shadow-[0_24px_60px_rgba(6,182,212,0.12)] ${className}`}
  >
    {image && (
      <>
        <img
          src={image}
          alt={alt}
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover opacity-60 transition-all duration-700 group-hover:scale-105 group-hover:opacity-75"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-navy-900 via-navy-900/60 to-navy-900/10" />
      </>
    )}
    <div className="relative flex h-full flex-col justify-end p-7">
      <span className="mb-auto inline-flex w-fit rounded-xl border border-cyan-400/20 bg-navy/70 p-3 text-cyan-300 backdrop-blur-sm">
        <Icon size={20} />
      </span>
      <h3 className="mt-16 font-heading text-xl font-bold text-white">{title}</h3>
      <p className="mt-2 max-w-md text-sm leading-relaxed text-slate-300">{desc}</p>
    </div>
  </div>
);

export default function Capabilities() {
  return (
    <section id="capabilities" data-testid="capabilities-section" className="bg-navy py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <SectionHead
          dark
          eyebrow="Projects / Capabilities"
          title="Built for Critical Connectivity"
          sub="Five capability areas that cover the infrastructure behind dependable networks."
        />

        <div className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-3 lg:grid-cols-4 lg:grid-rows-2">
          <Reveal className="md:col-span-2 lg:row-span-2" delay={0}>
            <Card
              testid="capability-ofc"
              title="OFC Infrastructure"
              desc="Underground and aerial fiber deployment — surveyed, laid, spliced, tested and commissioned."
              icon={Cable}
              image={TOWER}
              alt="Telecom tower against the sky, representing OFC infrastructure deployment"
              className="h-full min-h-[320px] lg:min-h-[480px]"
            />
          </Reveal>
          <Reveal delay={0.08}>
            <Card
              testid="capability-network"
              title="Network Infrastructure"
              desc="Switches, routers, SFPs and associated equipment."
              icon={ServerCog}
              image={RACKS}
              alt="Server racks in a data center, representing network infrastructure"
              className="h-full min-h-[220px]"
            />
          </Reveal>
          <Reveal delay={0.14}>
            <Card
              testid="capability-power"
              title="Telecom Power"
              desc="UPS, SMPS and battery backup solutions."
              icon={BatteryCharging}
              className="h-full min-h-[220px]"
            />
          </Reveal>
          <Reveal delay={0.2}>
            <Card
              testid="capability-connectivity"
              title="Connectivity"
              desc="Fiber lease, RF links and network connectivity."
              icon={Radio}
              image={WIRING}
              alt="Network switch wiring, representing connectivity solutions"
              className="h-full min-h-[220px]"
            />
          </Reveal>
          <Reveal delay={0.26}>
            <Card
              testid="capability-relocation"
              title="Infrastructure Relocation"
              desc="Fiber and telecom equipment shifting."
              icon={Move}
              className="h-full min-h-[220px]"
            />
          </Reveal>
        </div>
      </div>
    </section>
  );
}
